import React from 'react';
import ListViewBookings from './bookings/ListViewBookings/ListViewBookings';
import ListViewPagination from './ListViewPagination';
import ListViewToolbar from './toolbar/ListViewToolbar/ListViewToolbar';
import useListViewPagination from '../hooks/useListViewPagination';
import useMinuteClock from '../hooks/useMinuteClock';
import { isToday } from '../libs/dateTimeUtilities';
import { mergeDeletedBookings } from '../libs/listViewDiff';
import {
  bookedRolesOfBookings,
  buildFilterTree,
  eventProfilesOfBookings,
  eventStatusesOfBookings,
  filterBookings,
  renderedColumns,
  resourceGroupsOfBookings,
  resourceRolesOfBookings,
  withoutPastBookings
} from '../libs/listViewPresentation';
import type {
  ActiveFilter,
  CustomDataTreeNode,
  ListViewBooking,
  ListViewColumn,
  ListViewConfiguration,
  ListViewDate,
  ListViewDiff,
  ListViewMode,
  RefreshControl,
  TimeFormatSetting
} from './types';

/* The resource bookings fetched via /resourcebookings, presented according to
 * the configuration stored in Firestore (dashboard type 'listview'). This
 * component holds what both view modes share — the toolbar the view is scoped
 * and shaped in, and the paging — and hands the bookings of the current page
 * to ListViewBookings, which shows them in the view mode that applies.
 * Filtering and pagination happen client-side on the fetched bookings.
 *
 * The bookings are the only part that waits for the /resourcebookings request:
 * everything derived from the configuration renders as soon as it is there, and
 * ListViewBookings shows the loading (or failed) state on its own. Until the
 * bookings of the selected day have arrived the view presents none, so the
 * paging and the item count describe the day that is actually shown.
 *
 * Rows are marked against the previous fetch and against the clock; see
 * listViewRowStatus for what the marking means. A deleted booking is
 * reconstructed from its snapshot values, which cover the visible columns and
 * the fields the filters read at the time of the previous fetch (see
 * listViewDiff), so it filters like the live bookings around it. A filter
 * added since then cannot match it until the next fetch has stored its field. */

interface ListViewProps {
  /* The bookings of the selected day; null until a fetch for it came back.
     Everything around them — the day, the filters, the refresh controls —
     renders without them, so only the bookings region waits */
  bookings: ListViewBooking[] | null;
  /* Whether the bookings held are the ones of the selected day: false during
     the initial load and while a newly picked day is being fetched */
  bookingsFetched: boolean;
  /* The bookings fetch failed; reported inside the bookings region only */
  bookingsFailed: boolean;
  /* The complete custom data field schemas of resources and resourcebookings,
     fetched via /customdatafields/:entity; null while pending or failed, in
     which case that domain's custom data filters are simply not offered (there
     is no bookings-derived fallback for these, unlike resourceGroups) */
  resourceCustomDataFields: CustomDataTreeNode[] | null;
  resourceBookingCustomDataFields: CustomDataTreeNode[] | null;
  /* The complete resource groups/roles of the installation, fetched via
     /resourceoptions; null while that fetch is pending or failed, in which case
     that filter falls back to the values occurring in the fetched bookings */
  resourceGroups: string[] | null;
  resourceRoles: string[] | null;
  /* The complete event statuses/profiles of the installation, fetched via
     /eventoptions; null while that fetch is pending or failed, in which case
     those filters fall back to the values occurring in the fetched bookings */
  eventStatuses: string[] | null;
  eventProfiles: string[] | null;
  configuration: ListViewConfiguration;
  viewMode: ListViewMode;
  onViewModeChange: (viewMode: ListViewMode) => void;
  date: ListViewDate;
  onDateChange: (date: ListViewDate) => void;
  activeFilters: ActiveFilter[];
  onAddFilter: (filter: ActiveFilter) => void;
  onUpdateFilter: (filter: ActiveFilter) => void;
  onRemoveFilter: (filterID: string) => void;
  /* Whether the members of the sets are listed under the set they belong to.
     It does every set at once; each of them can then be opened and closed on
     its own (see useListViewSetCollapse) */
  showChildren: boolean;
  onShowChildrenChange: (showChildren: boolean) => void;
  /* The remembered preference; it applies to today only (see below) */
  hidePastBookings: boolean;
  onHidePastBookingsChange: (hidePastBookings: boolean) => void;
  dateFormatSetting: string;
  timeFormatSetting: TimeFormatSetting;
  /* Deleted/changed markers of the last refresh; null without a comparable
     previous fetch (first load, or the day/columns changed) */
  diff: ListViewDiff | null;
  /** The clock the past/active row marking compares against. Defaults to a
   *  minute-ticking clock; pass one to pin the marking to a fixed moment */
  now?: string;
  refreshControl: RefreshControl;
}

const ListView = ({
  bookings,
  bookingsFetched,
  bookingsFailed,
  resourceCustomDataFields,
  resourceBookingCustomDataFields,
  resourceGroups,
  resourceRoles,
  eventStatuses,
  eventProfiles,
  configuration,
  viewMode,
  onViewModeChange,
  date,
  onDateChange,
  activeFilters,
  onAddFilter,
  onUpdateFilter,
  onRemoveFilter,
  showChildren,
  onShowChildrenChange,
  hidePastBookings,
  onHidePastBookingsChange,
  dateFormatSetting,
  timeFormatSetting,
  diff,
  now,
  refreshControl
}: ListViewProps) => {
  /* The clock ticks on its own, and re-stamps on every completed refresh so a
     refreshed row is marked against the moment it was refreshed */
  const tickingNow = useMinuteClock(refreshControl.lastRefreshedAt);
  const nowToCompareAgainst = now === undefined ? tickingNow : now;

  /* Bookings from a previous day are held on to while the next day is
     fetched; they are not the selected day's, so they are not presented */
  const loadedBookings = bookingsFetched && bookings !== null ? bookings : [];
  const columns: ListViewColumn[] = renderedColumns(configuration);
  const rolesOfResources =
    resourceRoles !== null ? resourceRoles : resourceRolesOfBookings(loadedBookings);
  const filterTree = buildFilterTree({
    resourceCustomDataTree: resourceCustomDataFields || [],
    resourceBookingCustomDataTree: resourceBookingCustomDataFields || [],
    resourceGroups:
      resourceGroups !== null ? resourceGroups : resourceGroupsOfBookings(loadedBookings),
    resourceRoles: rolesOfResources,
    /* The booked role filter needs the roles of the bookings themselves too,
       since a booking can assign a role no resource has */
    bookedRoles: bookedRolesOfBookings(loadedBookings, rolesOfResources),
    eventStatuses:
      eventStatuses !== null ? eventStatuses : eventStatusesOfBookings(loadedBookings),
    eventProfiles:
      eventProfiles !== null ? eventProfiles : eventProfilesOfBookings(loadedBookings)
  });
  const statusByID = diff ? diff.statusByID : {};
  /* Bookings deleted since the previous fetch are merged back in at their
     chronological position, so they filter, group and paginate like the rest */
  const mergedBookings = diff
    ? mergeDeletedBookings(loadedBookings, diff.deletedBookings)
    : loadedBookings;
  const filteredBookings: ListViewBooking[] = filterBookings(
    mergedBookings,
    activeFilters
  );
  /* Hiding what has ended applies to today only, and it is decided here rather
     than in the toolbar alone: a view left open across midnight is on
     yesterday from then on, and would empty itself behind a disabled switch */
  const showsToday = isToday(date);
  const hidingPastBookings = hidePastBookings && showsToday;
  /* Dropped after the filters, so the count below describes the list as shown */
  const shownBookings: ListViewBooking[] = hidingPastBookings
    ? withoutPastBookings(filteredBookings, nowToCompareAgainst)
    : filteredBookings;
  const { page, pagesCount, pageBookings, onPageChange } = useListViewPagination(
    shownBookings,
    configuration.itemsPerPage || 50,
    date,
    activeFilters,
    hidingPastBookings
  );
  return (
    <div data-test-list-view>
      <ListViewToolbar
        date={date}
        onDateChange={onDateChange}
        filterTree={filterTree}
        activeFilters={activeFilters}
        onAddFilter={onAddFilter}
        onUpdateFilter={onUpdateFilter}
        onRemoveFilter={onRemoveFilter}
        viewMode={viewMode}
        onViewModeChange={onViewModeChange}
        showChildren={showChildren}
        onShowChildrenChange={onShowChildrenChange}
        /* The value as it applies, not the raw preference */
        hidePastBookings={hidingPastBookings}
        onHidePastBookingsChange={onHidePastBookingsChange}
        canHidePastBookings={showsToday}
        refreshControl={refreshControl}
        timeFormatSetting={timeFormatSetting}
      />
      <ListViewBookings
        bookings={pageBookings}
        isLoading={!bookingsFetched}
        loadFailed={bookingsFailed}
        columns={columns}
        configuration={configuration}
        viewMode={viewMode}
        showChildren={showChildren}
        statusByID={statusByID}
        now={nowToCompareAgainst}
        dateFormatSetting={dateFormatSetting}
        timeFormatSetting={timeFormatSetting}
      />
      <ListViewPagination
        page={page}
        pagesCount={pagesCount}
        totalCount={shownBookings.length}
        onPageChange={onPageChange}
      />
    </div>
  );
};

export default ListView;
